import { Icon, type IconName } from '@mdvip/shared/components/blocks/Icon';
import { cn } from '@mdvip/shared/lib/utils';
import { clsx } from 'clsx';
import type * as React from 'react';

const SIZE = { h1: 'text-h1', h2: 'text-h2', h3: 'text-h3', h4: 'text-h4' } as const;

export interface SectionHeaderProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'title'> {
  title: React.ReactNode;
  icon?: IconName;
  count?: number;
  /** Plain text after the count ("4 resources"); never a badge. */
  countLabel?: string;
  /** Trailing content, usually a "view all" link. Replaces the count when set. */
  aside?: React.ReactNode;
  description?: React.ReactNode;
  /** Heading level. Independent of `size`, so the outline and the type scale can differ. */
  as?: 'h1' | 'h2' | 'h3' | 'h4';
  /** Type scale. */
  size?: keyof typeof SIZE;
  /** The 2px rule under the whole header. Turn off only inside an already-bounded surface such as a card. */
  rule?: boolean;
  /** Which row the aside sits on when there is a description. */
  asideOn?: 'title' | 'description';
}

/**
 * The ruled band that opens a section: a heading, an optional glyph, a count or aside on the far
 * end, an optional description and a 2px rule underneath.
 *
 * The rule closes the whole header, not the title row: a divider between a heading and its own
 * description would read as a separator between two unrelated things. For the same reason the
 * aside drops onto the description's row when there is one, so a "view all" link ends 16px above
 * the rule instead of two rows above it. `asideOn="title"` instead makes the heading and
 * description one lockup and bottom-aligns the aside to it, so controls sit level with the last
 * line of the description; the lockup holds a 16rem floor so the aside wraps onto its own line on
 * a phone.
 */
function SectionHeader({
  title,
  icon,
  count,
  countLabel,
  aside,
  description,
  as: Heading = 'h2',
  size = 'h3',
  rule = true,
  asideOn = 'description',
  className,
  ...props
}: SectionHeaderProps) {
  const countText = count === undefined ? null : countLabel === undefined ? String(count) : `${count} ${countLabel}`;

  const trailing =
    aside ??
    (countText === null ? null : <span className="text-xs font-medium text-muted-foreground">{countText}</span>);

  return (
    <div
      data-slot="section-header"
      className={cn('flex flex-col gap-2', rule && 'border-b-2 border-border pb-4', className)}
      {...props}
    >
      <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
        <div className={cn('flex min-w-0 flex-col gap-2', asideOn === 'title' && description && 'min-w-64 flex-1')}>
          {/* `clsx`, not `cn`: twMerge doesn't know `text-h4` is a font size, reads it as a color and
              drops `text-foreground`. There is nothing here to override, so no merge is needed. */}
          <Heading className={clsx('flex items-center gap-2 font-semibold text-foreground', SIZE[size])}>
            {icon && <Icon name={icon} size="md" className="text-muted-foreground" />}
            {title}
          </Heading>
          {description && asideOn === 'title' && (
            <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{description}</p>
          )}
        </div>
        {(!description || asideOn === 'title') && trailing}
      </div>
      {description && asideOn !== 'title' && (
        <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
          <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{description}</p>
          {trailing}
        </div>
      )}
    </div>
  );
}

export { SectionHeader };
